<template>
  <div class="documents">

    <!-- Document Categories -->
    <div class="document-categories">
      <div class="category-btns">
        <button 
          v-for="category in categories" 
          :key="category.id"
          :class="['category-btn', { active: activeCategory === category.id }]"
          @click="setActiveCategory(category.id)"
        >
          <i :class="category.icon"></i>
          {{ $t(`customerDocuments.categories.${category.id}`) }}
        </button>
      </div>
      <div class="view-toggle-group">
        <button
          class="view-toggle-btn1"
          :class="{ active: viewMode === 'list' }"
          @click="setViewMode('list')"
          aria-label="List View"
        >
          <i class="fas fa-list"></i>
        </button>
        <button
          class="view-toggle-btn1"
          :class="{ active: viewMode === 'grid' }"
          @click="setViewMode('grid')"
          aria-label="Grid View"
        >
          <i class="fas fa-th"></i>
        </button>
      </div>
    </div>

    <!-- Document List -->
    <div class="documents-list" v-if="viewMode === 'grid' || isMobile">
      <div class="document-card" v-for="doc in filteredDocuments" :key="doc.id">
        <div class="doc-icon">
          <svg v-if="doc.type === 'articles'" width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="20" fill="#E8F5E9"/>
            <rect x="12" y="10" width="16" height="20" rx="2" stroke="#2E7D32" stroke-width="2" fill="white"/>
            <path d="M20 18v8M20 26l-3-3m3 3l3-3" stroke="#2E7D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else-if="doc.type === 'receipt'" width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="20" fill="#E8F5E9"/>
            <rect x="12" y="10" width="16" height="20" rx="2" stroke="#2E7D32" stroke-width="2" fill="white"/>
            <path d="M20 18v8M20 26l-3-3m3 3l3-3" stroke="#2E7D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else-if="doc.type === 'fein'" width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="20" fill="#E8F5E9"/>
            <rect x="12" y="10" width="16" height="20" rx="2" stroke="#2E7D32" stroke-width="2" fill="white"/>
            <path d="M16 22l3 3 5-5" stroke="#2E7D32" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-else width="40" height="40" viewBox="0 0 40 40" fill="none">
            <circle cx="20" cy="20" r="20" fill="#F5F7FA"/>
          </svg>
        </div>
        <div class="doc-info">
          <h3>{{ $t(`customerDocuments.documents.${doc.type}.name`) }}</h3>
          <p>{{ $t(`customerDocuments.documents.${doc.type}.description`) }}</p>
          <span class="doc-date">{{ formatDate(doc.date) }}</span>
          <span class="category-badge" :class="doc.category">
            {{ $t(`customerDocuments.categories.${doc.category}`) }}
          </span>
        </div>
        <div class="doc-actions">
          <button class="action-btn view" @click="viewDocument(doc)">
            <i class="fas fa-eye"></i>
            {{ $t('customerDocuments.actions.view') }}
          </button>
          <button class="action-btn download" @click="downloadDocument(doc)">
            <i class="fas fa-download"></i>
            {{ $t('customerDocuments.actions.download') }}
          </button>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div class="documents-table" v-else>
      <table>
        <thead>
          <tr>
            <th>{{ $t('customerDocuments.table.headers.name') }}</th>
            <th>{{ $t('customerDocuments.table.headers.description') }}</th>
            <th>{{ $t('customerDocuments.table.headers.category') }}</th>
            <th>{{ $t('customerDocuments.table.headers.date') }}</th>
            <th>{{ $t('customerDocuments.table.headers.actions') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="doc in filteredDocuments" :key="doc.id">
            <td>
              <div class="doc-name-cell">
                <div class="doc-icon">
                  <svg v-if="doc.type === 'articles'" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="12" fill="#E8F5E9"/>
                    <rect x="7" y="6" width="10" height="12" rx="1" stroke="#2E7D32" stroke-width="1.5" fill="white"/>
                    <path d="M12 11v5M12 16l-2-2m2 2l2-2" stroke="#2E7D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else-if="doc.type === 'receipt'" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="12" fill="#E8F5E9"/>
                    <rect x="7" y="6" width="10" height="12" rx="1" stroke="#2E7D32" stroke-width="1.5" fill="white"/>
                    <path d="M12 11v5M12 16l-2-2m2 2l2-2" stroke="#2E7D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else-if="doc.type === 'fein'" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="12" fill="#E8F5E9"/>
                    <rect x="7" y="6" width="10" height="12" rx="1" stroke="#2E7D32" stroke-width="1.5" fill="white"/>
                    <path d="M10 13l2 2 3-3" stroke="#2E7D32" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="12" fill="#F5F7FA"/>
                  </svg>
                </div>
                <span>{{ $t(`customerDocuments.documents.${doc.type}.name`) }}</span>
              </div>
            </td>
            <td>{{ $t(`customerDocuments.documents.${doc.type}.description`) }}</td>
            <td>
              <span class="category-badge" :class="doc.category">
                {{ $t(`customerDocuments.categories.${doc.category}`) }}
              </span>
            </td>
            <td>{{ formatDate(doc.date) }}</td>
            <td>
              <div class="table-actions">
                <button class="action-btn view" @click="viewDocument(doc)" :title="$t('customerDocuments.actions.view')">
                  <i class="fas fa-eye"></i>
                </button>
                <button class="action-btn download" @click="downloadDocument(doc)" :title="$t('customerDocuments.actions.download')">
                  <i class="fas fa-download"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Upload Document Section -->
    <div class="upload-section">
      <h2>{{ $t('customerDocuments.upload.title') }}</h2>
      <div class="upload-area" @click="triggerFileInput" @drop.prevent="handleDrop" @dragover.prevent>
        <i class="fas fa-cloud-upload-alt"></i>
        <p>{{ $t('customerDocuments.upload.dragDrop') }}</p>
        <input 
          type="file" 
          ref="fileInput" 
          style="display: none" 
          @change="handleFileSelect"
          multiple
        />
      </div>
    </div>
  </div>
</template>

<script>
import '@/styles/CustomerDocuments.css'

export default {
  name: 'CustomerDocuments',
  props: {
    selectedBusiness: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      viewMode: 'grid',
      activeCategory: 'all',
      categories: [
        { id: 'all', name: 'customerDocuments.categories.all', icon: 'fas fa-folder' },
        { id: 'formation', name: 'customerDocuments.categories.formation', icon: 'fas fa-file-contract' },
        { id: 'tax', name: 'customerDocuments.categories.tax', icon: 'fas fa-file-invoice-dollar' },
        { id: 'compliance', name: 'customerDocuments.categories.compliance', icon: 'fas fa-clipboard-check' }
      ],
      documents: [],
      isMobile: false
    }
  },
  created() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
  watch: {
    selectedBusiness: {
      immediate: true,
      async handler(newBusiness) {
        if (newBusiness) {
          try {
            // Fetch documents for the selected business
            // const response = await documentService.getBusinessDocuments(newBusiness.id);
            // this.documents = response.data;
            
            // For now, using mock data
            this.documents = [
              {
                id: 1,
                name: 'Articles of Organization',
                description: 'LLC Formation Document',
                date: '2024-02-15',
                category: 'formation',
                type: 'articles',
                businessId: newBusiness.id
              },
              {
                id: 2,
                name: 'State Filing Receipt',
                description: 'Filing Receipt',
                date: '2024-02-16',
                category: 'formation',
                type: 'receipt',
                businessId: newBusiness.id
              },
              {
                id: 3,
                name: 'FEIN Details',
                description: 'Federal Tax ID',
                date: '2024-02-17',
                category: 'tax',
                type: 'fein',
                businessId: newBusiness.id
              }
            ];
          } catch (error) {
            console.error('Error fetching documents:', error);
          }
        }
      }
    }
  },
  computed: {
    filteredDocuments() {
      if (this.activeCategory === 'all') {
        return this.documents;
      }
      return this.documents.filter(doc => doc.category === this.activeCategory);
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
      if (this.isMobile) {
        this.viewMode = 'grid';
      }
    },
    setViewMode(mode) {
      this.viewMode = mode
    },
    setActiveCategory(categoryId) {
      this.activeCategory = categoryId
    },
    viewDocument(doc) {
      console.log('Viewing document:', doc)
    },
    downloadDocument(doc) {
      console.log('Downloading document:', doc)
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleFileSelect(event) {
      const files = event.target.files
      this.uploadFiles(files)
    },
    handleDrop(event) {
      const files = event.dataTransfer.files
      this.uploadFiles(files)
    },
    uploadFiles(files) {
      // Handle file upload logic
      console.log('Uploading files:', files)
    },
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString(this.$i18n.locale === 'es' ? 'es-ES' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
  }
}
</script>

<style scoped>
/* ... existing styles ... */

.documents-table {
  width: 100%;
  margin: 1rem 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.documents-table table {
  width: 100%;
  border-collapse: collapse;
}

.documents-table th,
.documents-table td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.documents-table th {
  background-color: #f9fafb;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
}

.documents-table td {
  font-size: 0.875rem;
  color: #4b5563;
}

.doc-name-cell {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.doc-name-cell .doc-icon {
  flex-shrink: 0;
}

.category-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: capitalize;
}

.category-badge.formation {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.category-badge.tax {
  background-color: #e3f2fd;
  color: #1976d2;
}

.category-badge.compliance {
  background-color: #fff3e0;
  color: #f57c00;
}

.table-actions {
  display: flex;
  gap: 0.5rem;
}

.table-actions .action-btn {
  padding: 0.375rem;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.table-actions .action-btn i {
  font-size: 1rem;
}

.table-actions .action-btn.view {
  background-color: #e8f5e9;
  color: #2e7d32;
}

.table-actions .action-btn.download {
  background-color: #e3f2fd;
  color: #1976d2;
}

.table-actions .action-btn:hover {
  opacity: 0.8;
}

@media (max-width: 768px) {
  .documents {
    padding: 0.5rem;
  }

  .view-toggle-group {
    display: none;
  }
  .document-categories {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .category-btn {
    padding: 0.5rem;
    font-size: 0.75rem;
  }

  .documents-list {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .document-card {
    padding: 1rem;
  }

  .doc-info {
    gap: 0.5rem;
  }

  .doc-info h3 {
    font-size: 1rem;
  }

  .doc-info p {
    font-size: 0.875rem;
  }

  .doc-actions {
    flex-direction: column;
    gap: 0.5rem;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }

  .upload-section {
    padding: 1rem;
  }

  .upload-area {
    padding: 1.5rem;
  }

  .upload-area i {
    font-size: 2rem;
  }

  .upload-area p {
    font-size: 0.875rem;
  }
}

@media (max-width: 480px) {
  .documents {
    padding: 0.25rem;
  }

  .document-card {
    padding: 0.75rem;
  }

  .doc-icon {
    width: 32px;
    height: 32px;
  }

  .doc-icon svg {
    width: 32px;
    height: 32px;
  }

  .upload-area {
    padding: 1rem;
  }
}
</style>

<!-- Place this temporarily in your template for testing -->
<svg width="28" height="28" viewBox="0 0 28 28" fill="none">
  <circle cx="7" cy="8" r="1.5" fill="#22c55e"/>
  <rect x="11" y="7" width="10" height="2" rx="1" fill="#22c55e"/>
  <circle cx="7" cy="14" r="1.5" fill="#22c55e"/>
  <rect x="11" y="13" width="10" height="2" rx="1" fill="#22c55e"/>
  <circle cx="7" cy="20" r="1.5" fill="#22c55e"/>
  <rect x="11" y="19" width="10" height="2" rx="1" fill="#22c55e"/>
</svg> 